import { Inject, Injectable, OnDestroy } from '@angular/core';
import {
  filter,
  map as rxMap,
  Observable,
  startWith,
  takeUntil,
  tap,
} from 'rxjs';

import {
  DynamicFormDefaultHooks,
  DynamicFormExpressions,
  DynamicFormHooks,
  DynamicFormsExtensionsService,
} from '@lockton-apps/dynamic-forms';
import {
  clamp,
  get,
  isNumber,
  isString,
  last,
  map,
  round,
  set,
  split,
  sum,
  values,
} from 'lodash';
import { PROPOSAL_PARTNER_DATA } from '../../constants/proposal-partner-data';
import { CastObservablePipe } from '@lockton-apps/shared/ui/pipes';
import { getYear } from 'date-fns';
import { DecimalPipe } from '@angular/common';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ProposalProductDetailsComponent } from '@lockton-apps/shared/ui/proposal-product-details';
import {
  DocumentsService,
  SupportingDocumentsService,
} from '@lockton-apps/shared/data-access/documents';
import { FeedbackService } from '@lockton-apps/shared/ui/lui';
import { nanoid } from 'nanoid';
import { MatDialog } from '@angular/material/dialog';

interface PartnerData {
  proposalId: string | undefined;
  createdDate: string;
  availableAddonCobs: number[];
  readonly: boolean;
  generalFolderId: number;
  clientId: number;
}

@Injectable()
export class EnwDynamicFormsExtensionsService
  extends DynamicFormsExtensionsService
  implements OnDestroy
{
  override hooks: DynamicFormHooks[] = [
    {
      id: 'residentialWorkTypesSummary',
      hookConfig: {
        onInit: (field) => {
          return field.options?.fieldChanges?.pipe(
            filter((e) => e.type === 'valueChanges'),
            tap(() => {
              const overallModelPaths = [
                'property.residentialWorkTypes.singlePropertiesUKIndividualsEntities',
                'property.residentialWorkTypes.salePurchasePropertyByOverseasEntity',
                'property.residentialWorkTypes.multipleBuyers.total',
                'property.residentialWorkTypes.singlePurchaser.total',
                'property.residentialWorkTypes.homeEquityRelease',
              ];
              const value = sum(
                map(overallModelPaths, (path) => get(field.model, path))
              );

              field.formControl?.setValue(value, { emitEvent: false });
              field?.options?.detectChanges?.(field);
            })
          );
        },
      },
    },
    {
      id: 'locationId',
      hookConfig: {
        onInit: (field) => {
          if (!field.formControl || !!field.model.id) {
            return;
          }

          field.formControl.setValue(nanoid(), { emitEvent: false });
        },
      },
    },
    {
      id: 'openClaimantPersonalInjuryMaxLitigation',
      hookConfig: {
        onInit: (field) => {
          field.formControl?.addValidators((control) => {
            return control.value > control.parent!.value.total
              ? { total: true }
              : null;
          });

          field.parent?.formControl?.valueChanges
            .pipe(takeUntil(this.destroyNotifier))
            .subscribe(() => {
              field.formControl?.updateValueAndValidity({ emitEvent: false });
            });
        },
      },
    },
    {
      id: 'averageSettlementMaxSettlement',
      hookConfig: {
        onInit: (field) => {
          field.formControl?.addValidators((control) => {
            return control.value > control.parent!.value.highestSettlement
              ? { total: true }
              : null;
          });

          field.parent?.formControl?.valueChanges
            .pipe(takeUntil(this.destroyNotifier))
            .subscribe(() => {
              field.formControl?.updateValueAndValidity({ emitEvent: false });
            });
        },
      },
    },
    {
      id: 'grossPercentage',
      hookConfig: {
        onInit: (field) => {
          field.formControl?.valueChanges
            .pipe(takeUntil(this.destroyNotifier))
            .subscribe((v) => {
              if (!field.key || !isString(field.key)) return;
              const total = last(
                get(field.options?.formState.derivedValues, 'grossTotals')
              );

              if (!isNumber(total)) return;

              const key = split(field.key, '.')[0];

              const amountValue = (total * v) / 100;

              field.parent?.formControl
                ?.get(`${key}.FY-1.amount`)
                ?.setValue(round(amountValue, 2), { emitEvent: false });
            });
        },
      },
    },
    {
      id: 'grossAmount',
      hookConfig: {
        onInit: (field) => {
          field.formControl?.valueChanges
            .pipe(
              takeUntil(this.destroyNotifier),
              startWith(field.formControl?.value)
            )
            .subscribe((v) => {
              if (!field.key || !isString(field.key)) return;
              const total = last(
                get(field.options?.formState.derivedValues, 'grossTotals')
              );

              if (!isNumber(total)) return;

              const key = split(field.key, '.')[0];

              const percentValue = total ? (v / total) * 100 : 0;

              field.parent?.formControl
                ?.get(`${key}.FY-1.percentage`)
                ?.setValue(round(percentValue, 2), { emitEvent: false });
            });
        },
      },
    },
    {
      id: 'solicitorsAreasOfPracticeWorkUndertaken',
      hookConfig: {
        onInit: (field) => {
          const totalGrossFeeIncomeForLastCompletedFY: number = sum(
            map(
              get(
                field.model,
                'financialInformation.grossFeeIncome.distribution',
                []
              ),
              'FY-1'
            )
          );

          field.props!['summary'].validations.eq.splice(3, 0, [
            totalGrossFeeIncomeForLastCompletedFY,
            2,
          ]);

          set(field.options?.formState.derivedValues, 'grossTotals', [
            null,
            null,
            null,
            totalGrossFeeIncomeForLastCompletedFY,
          ]);

          this.defaultHooks[
            DynamicFormDefaultHooks.FinancialYearDefinitionPrompt
          ].hookConfig.onInit(field);
        },
      },
    },
  ];

  override extensions = [
    {
      id: 'statements_grossFeeIncomeConfirmed',
      prop: 'label',
      value: [],
      mappedValue: (_: FormlyFieldConfig, data: any) => {
        const fee: number =
          get(data, 'grossFees.feeIncome.amount.previous', 0) * 1.1;

        return `1. The gross fee income for the practice for the last completed financial year has not exceeded GBP ${this.decimal.transform(
          fee,
          '1.2'
        )}`;
      },
    },
    {
      id: 'statements_noFeeEarningStaffChanges',
      prop: 'label',
      value: [],
      mappedValue: (_: FormlyFieldConfig, data: any) => {
        const partners: number = get(
          data,
          'staff.overview.distribution.management.previous',
          0
        );

        return `5. There ${
          partners === 1 ? `is 1 partner` : `are ${partners} partners`
        } in the practice, and there have been no fee earning staff changes in the past year, other than those already disclosed to HDI Global Specialty SE – UK`;
      },
    },
    {
      id: 'claimsCountMin',
      prop: 'min',
      value: 0,
      mappedValue: (_: FormlyFieldConfig, data: any) =>
        !data
          ? undefined
          : this.claimsCountMinMax(
              get(
                data,
                'details.firm.yearEstablished',
                getYear(new Date(data['createdDate']).getTime())
              )
            ),
    },
    {
      id: 'claimsCountMax',
      prop: 'max',
      value: 0,
      mappedValue: (_: FormlyFieldConfig, data: any) =>
        !data
          ? undefined
          : this.claimsCountMinMax(
              get(
                data,
                'details.firm.yearEstablished',
                getYear(new Date(data['createdDate']).getTime())
              )
            ),
    },
    {
      id: 'productDetails',
      prop: 'component',
      value: ProposalProductDetailsComponent,
    },
    {
      id: 'productDetailsInputs',
      prop: 'inputs',
      value: '',
      mappedValue: () => ({
        cobs: this.partnerData$.pipe(
          filter(Boolean),
          rxMap(({ availableAddonCobs }) => availableAddonCobs)
        ),
      }),
    },
  ];

  override expressions: Record<string, DynamicFormExpressions> = {
    /**
     * Dependancies
     * If any changes check regression against the following:
     * - expressions.majorsAreasOfPracticeValidations
     * - hooks.grossPercentage
     * - hooks.grossAmount
     */
    majorsAreasOfPrcticeTotalDerived: {
      'options.formState.derivedValues': (field) => {
        const partials = get(field.props, 'partials', []);
        const grossTotalDef = get(field.props, 'grossTotalDefinition', []);
        const values = map(partials, (partial) =>
          get(
            field.model,
            `financialInformation.grossFeeIncome.distribution.${partial}`
          )
        );
        const total = sum(values);
        return {
          ...field.options?.formState.derivedValues,
          grossTotals: map(grossTotalDef, (cell) =>
            cell === 'x' ? total : cell
          ),
        };
      },
    },
    majorsAreasOfPracticeValidations: {
      'props.summary.validations.eq': (field) => {
        const totals = get(
          field.options?.formState.derivedValues,
          'grossTotals'
        );

        return map(totals, (total, index: number) =>
          totals.length - 1 === index ? [total, 2] : [100, 0]
        );
      },
    },
    overseasOfficeBranchOffices: {
      hide: (field: FormlyFieldConfig) => {
        return !get(
          field.model,
          `${field.parent!.props!['id']}.branchOffices`,
          false
        );
      },
    },
    overseasOfficeOtherWork: {
      hide: (field: FormlyFieldConfig) => {
        return !get(
          field.model,
          `${field.parent!.props!['id']}.activitySplit?.allOtherWork`,
          false
        );
      },
    },
    overseasOfficeClaimsAgainstFirm: {
      hide: (field: FormlyFieldConfig) => {
        return !get(
          field.model,
          `${field.parent!.props!['id']}.claimsMadeAgainstFirm`,
          false
        );
      },
    },
    overseasOfficeCircumstancesAllegationsContentions: {
      hide: (field: FormlyFieldConfig) => {
        return !get(
          field.model,
          `${field.parent!.props!['id']}.circumstancesAllegationsContentions`,
          false
        );
      },
    },
    overseasOfficeDishonesty: {
      hide: (field: FormlyFieldConfig) => {
        return !get(
          field.model,
          `${field.parent!.props!['id']}.dishonestyOfPrincipalOrEmployee`,
          false
        );
      },
    },
    overseasOfficeDisciplinaryProceedings: {
      hide: (field: FormlyFieldConfig) => {
        return !get(
          field.model,
          `${field.parent!.props!['id']}.disciplinaryProceedings`,
          false
        );
      },
    },
    solicitorsHighMatrimonialSettlementsOrRegularAnnualPayments_processManagementDetails:
      {
        'props.required': (field: FormlyFieldConfig) => {
          const totalMatrimonialMattersOverseasAssetsCount: number = sum(
            values(
              get(
                field.model,
                'areasOfPractice.familyMatrimonial.highMatrimonialSettlementsOrRegularAnnualPayments.breakdown.matrimonialMattersOverseasAssets',
                []
              )
            )
          );

          return totalMatrimonialMattersOverseasAssetsCount <= 1;
        },
      },
    solicitorsHighMatrimonialSettlementsOrRegularAnnualPayments_largestMatters_claimValue:
      {
        'props.fields[1].props.label': (field: FormlyFieldConfig) => {
          const totalMatrimonialMattersOverseasAssetsCount: number = sum(
            values(
              get(
                field.model,
                'areasOfPractice.familyMatrimonial.highMatrimonialSettlementsOrRegularAnnualPayments.breakdown.matrimonialMattersOverseasAssets',
                []
              )
            )
          );

          return totalMatrimonialMattersOverseasAssetsCount > 1
            ? $localize`Estate Value`
            : $localize`Claim Value`;
        },
      },
  };

  constructor(
    @Inject(PROPOSAL_PARTNER_DATA)
    protected partnerData$: Observable<PartnerData>,
    private readonly decimal: DecimalPipe,
    castObservable: CastObservablePipe,
    documentsService: DocumentsService,
    supportingDocumentsService: SupportingDocumentsService,
    feedbackService: FeedbackService,
    dialog: MatDialog
  ) {
    super(
      castObservable,
      documentsService,
      supportingDocumentsService,
      feedbackService,
      dialog
    );
  }

  ngOnDestroy(): void {
    this.destroyNotifier.next();
    this.destroyNotifier.complete();
  }

  private claimsCountMinMax(yearEstablished: number): number {
    const currentYear: number = getYear(new Date());

    return clamp(currentYear - yearEstablished - 1, 0, 6);
  }
}
